import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exportTenant, importTenant } from '../../api/dataExchange';
import { p, tp, useP } from '../../i18n/useP';

/* Menu d'aiguillage des données People. Écran 1 = choix de l'action ; écran 2 =
   télécharger le template du type choisi → remplir → réimporter (année cible).
   « Ajouter un collaborateur » renvoie au formulaire de saisie (onAddPerson). */
const TYPES = {
  collaborateurs: { title: p('Nouveaux collaborateurs'), template: 'collaborateurs',
                    hint: p('Une ligne = un employé : identité + contrat + LPP.') },
  extras:         { title: tp('phrases:p_aad0f37111'), template: 'extras',
                    hint: p('Bonus, prime, gratification… pour des collaborateurs existants.'),
                    warn: p('Soumis aux charges sociales.') },
  frais:          { title: p('Frais'), template: 'frais',
                    hint: p('Frais pour des collaborateurs existants.') },
  indemnites:     { title: tp('phrases:p_a3924bf10f'), template: 'indemnites',
                    hint: tp('phrases:p_237f4d8941') },
};

// Icônes SVG inline (l'app ne charge pas de fonte d'icônes) — style ligne.
const ICONS = {
  add:   <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  bonus: <><circle cx="12" cy="8" r="7" /><path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.12" /></>,
  frais: <><rect x="1" y="4" width="22" height="16" rx="2" /><path d="M1 10h22" /></>,
  indem: <><circle cx="12" cy="12" r="10" /><path d="M8 12l4 4 4-4M12 8v8" /></>,
  // Tuiles de l'Accueil (2026-09-01) — mêmes traits, autres portes.
  story: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
  play:  <><circle cx="12" cy="12" r="10" /><path d="M10 8l6 4-6 4V8z" /></>,
  contract: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8" /></>,
  move:  <><path d="M17 3l4 4-4 4M21 7H9" /><path d="M7 13l-4 4 4 4M3 17h12" /></>,
  plan:  <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></>,
  route: <><circle cx="6" cy="19" r="2" /><circle cx="18" cy="5" r="2" /><path d="M8 19h8a4 4 0 0 0 0-8H8a4 4 0 0 1 0-8h8" /></>,
};
export function Ico({ k }) {
  return (
    <svg className="people-import__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[k]}</svg>
  );
}
const TYPE_ICON = { extras: 'bonus', frais: 'frais', indemnites: 'indem' };

/* Le MENU des données People, extrait de la modale le 2026-09-01 : la section
   « Accueil » le montre en pleine page, la modale le garde comme premier écran.
   Une seule définition des portes — les recopier, c'est les voir diverger.
   `onPick(key)` reçoit 'add' | 'collaborateurs' | 'extras' | 'frais' |
   'indemnites'. */
export function PeopleDataMenu({ onPick }) {
  const p = useP();
  return (
    <div className="people-import__menu">
      <div className="people-import__group">{p('Collaborateurs')}</div>
      <button type="button" className="people-import__row" onClick={() => onPick('add')}>
        <Ico k="add" />
        <span className="people-import__rtext">
          <span className="people-import__rtitle">{p('Ajouter un collaborateur')}</span>
          <span className="people-import__rsub">{p("Saisie d'un seul, à l'écran")}</span>
        </span>
        <span className="people-import__chev" aria-hidden="true">›</span>
      </button>
      <button type="button" className="people-import__row" onClick={() => onPick('collaborateurs')}>
        <Ico k="users" />
        <span className="people-import__rtext">
          <span className="people-import__rtitle">{p('Nouveaux collaborateurs')}</span>
          <span className="people-import__rsub">{p('Plusieurs — fichier Excel')}</span>
        </span>
        <span className="people-import__chev" aria-hidden="true">›</span>
      </button>
      <div className="people-import__group">{p('Charger des données — collaborateurs existants')}</div>
      {['extras', 'frais', 'indemnites'].map(k => (
        <button type="button" key={k} className="people-import__row" onClick={() => onPick(k)}>
          <Ico k={TYPE_ICON[k]} />
          <span className="people-import__rtext">
            <span className="people-import__rtitle">{TYPES[k].title}</span>
            <span className="people-import__rsub">{TYPES[k].hint}</span>
            {TYPES[k].warn && <span className="people-import__rwarn">{TYPES[k].warn}</span>}
          </span>
          <span className="people-import__chev" aria-hidden="true">›</span>
        </button>
      ))}
    </div>
  );
}

export default function PeopleImportModal({ tenantId, onClose, addToast, year, onImported, onAddPerson,
                                            initialScreen = 'menu' }) {
  const { t } = useTranslation();
  const p = useP();
  const [screen, setScreen] = useState(initialScreen);
  const [file, setFile]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [fileKey, setFileKey] = useState(0);   // remonte l'input pour le vider
  const [expYear, setExpYear] = useState(year ? String(year) : '');
  const yearOptions = year ? [year + 1, year, year - 1, year - 2, year - 3] : [];

  function goMenu() { setScreen('menu'); setFile(null); setResult(null); }
  function chooseAdd() { onAddPerson?.(); onClose(); }

  // Traduit une erreur réseau (serveur injoignable) en message clair.
  function netMsg(e) {
    const m = e?.message || '';
    if (/failed to fetch|load failed|networkerror/i.test(m)) {
      return p("le serveur n'a pas répondu — réessaie dans un instant");
    }
    return m;
  }

  async function handleDownload(template) {
    // Tous les templates sont vierges + ligne d'exemple (saisie/ajout, pas màj masse).
    try { await exportTenant(tenantId, 'people', expYear || undefined, template, true); }
    catch (e) { addToast?.(`${p('Échec du téléchargement : ')}${netMsg(e)}`, 'error'); }
  }
  async function handleImport() {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const stats = await importTenant(tenantId, file, 'overwrite', expYear || undefined);
      setResult(stats);
      onImported?.();
      setFile(null);            // force une nouvelle sélection (évite le double import)
      setFileKey(k => k + 1);   // vide l'input fichier
    } catch (e) {
      addToast?.(`${p("Échec de l'import : ")}${netMsg(e)}`, 'error');
    } finally { setBusy(false); }
  }

  const typeMeta = TYPES[screen];
  const done = result != null;

  return (
    <div className="topnav__modal-overlay">
      <div className="topnav__modal" onClick={e => e.stopPropagation()}>
        <div className="topnav__modal-header">
          <span className="topnav__modal-title">{screen === 'menu' ? p('Données People') : typeMeta.title}</span>
          <button className="topnav__modal-close" onClick={onClose}>✕</button>
        </div>

        {screen === 'menu' && (
          <div className="topnav__modal-body">
            <PeopleDataMenu onPick={(k) => (k === 'add' ? chooseAdd() : setScreen(k))} />
          </div>
        )}

        {screen !== 'menu' && (
          <>
            <div className="topnav__modal-body">
              <button type="button" className="people-import__back" onClick={goMenu}>{p('‹ Retour')}</button>
              <p className="people-import__step-hint">{typeMeta.hint}</p>
              {typeMeta.warn && <p className="people-import__rwarn">{typeMeta.warn}</p>}
              {screen !== 'collaborateurs' && (
                <ul className="people-import__addnote">
                  <li>{p('Une ligne par collaborateur, pré-remplie sur l\'année de travail.')}</li>
                  <li>{p('Année modifiable ligne par ligne (plusieurs années possibles).')}</li>
                  <li>{p('Chaque ligne est ajoutée ; montant vide = ignoré.')}</li>
                  <li>{p('Corrections et suppressions à l\'écran.')}</li>
                </ul>
              )}

              <div className="people-import__yearbar">
                <label className="people-import__year">
                  {p('Année de travail')}
                  <select value={expYear} onChange={e => { setExpYear(e.target.value); setResult(null); }}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    <option value="">{p('Toutes les années')}</option>
                  </select>
                </label>
              </div>

              <div className="people-import__step">
                <span className="people-import__num">1</span>
                <div>
                  <p className="people-import__step-title">{p('Télécharger le fichier')}</p>
                  <button type="button" className="hr-tabaside-btn" onClick={() => handleDownload(typeMeta.template)}>
                    {p('Télécharger le fichier Excel')}
                  </button>
                </div>
              </div>

              <div className="people-import__step">
                <span className="people-import__num">2</span>
                <div>
                  <p className="people-import__step-title">{p('Remplir, puis réimporter')}</p>
                  <input key={fileKey} type="file" accept=".xlsx"
                    onChange={e => { setFile(e.target.files[0] || null); setResult(null); }} />
                </div>
              </div>

              {done && (
                <div className={`people-import__result${result.errors?.length ? ' has-errors' : ''}`}>
                  <p>{result.created} {p('créés')} · {result.overwritten} {p('mis à jour')} · {result.skipped} {p('inchangés')}</p>
                  {result.errors?.length > 0 && (
                    <ul className="people-import__errors">
                      {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="topnav__modal-footer">
              <button className="topnav__btn-cancel" onClick={onClose}>{done ? t('common:actions.close') : t('common:actions.cancel')}</button>
              <button className="topnav__btn-import-confirm" disabled={!file || busy} onClick={handleImport}>
                {busy ? p('Import en cours…') : t('common:actions.import')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
